import type { CallLogRecord, CallOutcome } from '../../api/calls';

export type DerivedOutcome = CallOutcome | 'none';

/**
 * Map a CallContext `cleanup` endReason to a stored outcome. `null`/unknown
 * reasons are 'none' — the caller (reject / hangup / answered-elsewhere echo /
 * sibling-resolved) must pass the outcome explicitly because the reason alone
 * is ambiguous.
 */
export function deriveOutcome(reason: string | null, wasAnswered: boolean): DerivedOutcome {
  switch (reason) {
    case 'No answer': return 'no_answer';
    case 'Missed call': return 'missed';
    case 'Call cancelled': return 'missed';   // callee received caller's cancel
    case 'Call declined': return 'declined';
    case 'User busy': return 'busy';
    case 'Call ended': return wasAnswered ? 'completed' : 'failed';
    case 'Connection lost': return wasAnswered ? 'completed' : 'failed';
    case 'Microphone unavailable': return 'failed';
    default: return 'none';
  }
}

/** Outcomes shown in red (failed-to-connect from the viewer's side). */
export const MISSED_OUTCOMES: ReadonlySet<CallOutcome> = new Set(['missed', 'no_answer', 'failed']);

export function isMissed(o: CallOutcome): boolean {
  return MISSED_OUTCOMES.has(o);
}

/** Human label under the peer name, e.g. "Missed", "Outgoing", "12:03". */
export function outcomeLabel(rec: CallLogRecord): string {
  if (rec.outcome === 'missed' || rec.outcome === 'no_answer') return 'Missed';
  if (rec.outcome === 'declined') return 'Declined';
  if (rec.outcome === 'busy') return 'Busy';
  if (rec.outcome === 'cancelled') return 'Cancelled';
  if (rec.outcome === 'failed') return 'Failed';
  return rec.direction === 'outgoing' ? 'Outgoing' : 'Incoming';
}

/** "0:57" / "3:05" duration; '' when never answered. */
export function fmtDuration(s: number): string {
  if (!s || s < 1) return '';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/** A grouped call entry: consecutive same-peer same-direction calls collapsed. */
export interface CallGroup {
  key: string;
  peerUserId: string | null;
  peerName: string | null;
  peerAvatarUrl: string | null;
  conversationId: string | null;
  direction: CallLogRecord['direction'];
  outcome: CallLogRecord['outcome'];
  callType: CallLogRecord['call_type'];
  isGroup: boolean;
  latest: CallLogRecord;     // most recent in the group (drives time)
  count: number;
  records: CallLogRecord[];
}

/**
 * Collapse a newest-first list into WhatsApp-style groups: consecutive entries
 * with the same peer AND same direction AND same missed-ness merge, showing a
 * count like "Shruti (2)". Order preserved.
 */
export function groupCalls(records: CallLogRecord[]): CallGroup[] {
  const out: CallGroup[] = [];
  for (const r of records) {
    const prev = out[out.length - 1];
    const sameBucket =
      prev &&
      prev.peerUserId === r.peer_user_id &&
      prev.direction === r.direction &&
      isMissed(prev.outcome) === isMissed(r.outcome) &&
      !r.is_group && !prev.isGroup;
    if (sameBucket) {
      prev.count += 1;
      prev.records.push(r);
      continue;
    }
    out.push({
      key: r.is_group ? r.call_id : r.id,
      peerUserId: r.peer_user_id,
      peerName: r.peer_name,
      peerAvatarUrl: r.peer_avatar_url,
      conversationId: r.conversation_id,
      direction: r.direction,
      outcome: r.outcome,
      callType: r.call_type,
      isGroup: !!r.is_group,
      latest: r,
      count: 1,
      records: [r],
    });
  }
  return out;
}
